import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sparkles, Loader2 } from 'lucide-react';
import { getAvailableModels, generateDepthNotes } from '../services/api';
import { toast } from '../hooks/use-toast';

const AIDepthGenerator = ({ slideText, slideTitle, slideContent, onGenerated, disabled = false }) => {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(true);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const response = await getAvailableModels();
      setModels(response.models);
      setIsLoadingModels(false);
    } catch (error) {
      console.error('Failed to fetch models:', error);
      toast({
        title: "Error",
        description: "Failed to load AI models. Using default.",
        variant: "destructive"
      });
      setIsLoadingModels(false);
    }
  };

  const handleGenerate = async () => {
    // Build the input text from available props
    let inputText = '';
    
    // If new props (slideTitle and slideContent) are provided, use them
    if (slideTitle !== undefined && slideContent !== undefined) {
      const title = slideTitle?.trim() || '';
      const content = slideContent?.trim() || '';
      
      if (!title && !content) {
        toast({
          title: "No Content",
          description: "Please add a title or content to the slide first.",
          variant: "destructive"
        });
        return;
      }
      
      // Combine title and content with clear separation
      inputText = [title, content].filter(Boolean).join('\n\n');
    } 
    // Fallback to old slideText prop for backward compatibility
    else if (slideText) {
      inputText = slideText.trim();
      
      if (!inputText) {
        toast({
          title: "No Content",
          description: "Please add some content to the slide first.",
          variant: "destructive"
        });
        return;
      }
    } else {
      toast({
        title: "No Content",
        description: "Please add some content to the slide first.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      const depthData = await generateDepthNotes(inputText, selectedModel);
      
      // Format the generated data into a readable string for the depth notes field
      const formattedDepthNotes = formatDepthNotes(depthData);
      
      // Call the parent component's callback with the generated notes
      onGenerated(formattedDepthNotes, depthData);
      
      toast({
        title: "Success!",
        description: "AI Depth Notes generated successfully.",
      });
    } catch (error) {
      console.error('Failed to generate depth notes:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate depth notes. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDepthNotes = (depthData) => {
    let formatted = '';
    
    if (depthData.contextual_insight) {
      formatted += `📚 CONTEXTUAL INSIGHT\n${depthData.contextual_insight}\n\n`;
    }
    
    if (depthData.cross_references && depthData.cross_references.length > 0) {
      formatted += `🔗 CROSS-REFERENCES\n`;
      depthData.cross_references.forEach((ref, idx) => {
        formatted += `${idx + 1}. ${ref}\n`;
      });
      formatted += '\n';
    }
    
    if (depthData.biblical_context && depthData.biblical_context.length > 0) {
      formatted += `📖 BIBLICAL CONTEXT\n`;
      depthData.biblical_context.forEach((verse, idx) => {
        formatted += `${idx + 1}. ${verse}\n`;
      });
      formatted += '\n';
    }
    
    if (depthData.further_reading && depthData.further_reading.length > 0) {
      formatted += `📖 FURTHER READING\n`;
      depthData.further_reading.forEach((reading, idx) => {
        formatted += `${idx + 1}. ${reading}\n`;
      });
    }
    
    return formatted.trim();
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      <Select 
        value={selectedModel} 
        onValueChange={setSelectedModel}
        disabled={isGenerating || isLoadingModels || disabled}
      >
        <SelectTrigger className="w-48">
          <SelectValue placeholder="Select AI Model" />
        </SelectTrigger>
        <SelectContent>
          {isLoadingModels ? (
            <SelectItem value="loading" disabled>Loading models...</SelectItem>
          ) : models.length > 0 ? (
            models.map((model) => (
              <SelectItem key={model.id} value={model.id}>
                {model.name}
              </SelectItem>
            ))
          ) : (
            <SelectItem value="gpt-4o">GPT-4o (Default)</SelectItem>
          )}
        </SelectContent>
      </Select>
      
      <Button
        type="button"
        onClick={handleGenerate}
        disabled={isGenerating || disabled}
        variant="outline"
        className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
      >
        {isGenerating ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            Generate with AI
          </>
        )}
      </Button>
    </div>
  );
};

export default AIDepthGenerator;
